import { useState } from 'react';
import { KeyRound, Loader2, X } from 'lucide-react';

/**
 * Сброс пароля по токену из письма (ЧТЗ-AUTH-004, FR-AUTH-19).
 * POST /auth/reset-password {token, new_password}.
 *
 * props:
 *   token              — токен из ссылки письма (?token=...)
 *   onReset(token, newPassword) — async, бросает Error с .code при ошибке
 *   onClose()          — закрыть оверлей
 *   onSwitchMode(mode) — переключить режим ('login' | 'forgot')
 */
const TOKEN_ERROR_MESSAGE = 'Ссылка недействительна или устарела, запросите сброс заново.';

export default function ResetPasswordModal({ token, onReset, onClose, onSwitchMode }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (password !== confirm) {
      setError('Пароли не совпадают.');
      return;
    }
    if (password.length < 8) {
      setError('Пароль должен быть не короче 8 символов.');
      return;
    }
    setBusy(true);
    try {
      await onReset(token, password);
      // успех: переключаем на вход (сообщение покажет родитель/тост)
      onSwitchMode?.('login');
    } catch (err) {
      const code = err?.code;
      if (code === 'INVALID_TOKEN' || code === 'TOKEN_USED' || code === 'TOKEN_EXPIRED') {
        setError(TOKEN_ERROR_MESSAGE);
      } else if (code === 'VALIDATION_ERROR') {
        setError('Пароль должен быть не короче 8 символов.');
      } else if (code === 'SERVICE_UNAVAILABLE') {
        setError('Сервис недоступен, попробуйте позже.');
      } else {
        setError('Не удалось сбросить пароль. Попробуйте позже.');
      }
    } finally {
      setBusy(false);
    }
  };

  const tokenInvalid =
    error === TOKEN_ERROR_MESSAGE;

  return (
    <div className="acc-modal" role="dialog" aria-modal="true" aria-label="Сброс пароля">
      <div className="acc-modal-head">
        <h2><KeyRound size={16} /> Новый пароль</h2>
        <button className="acc-icon-btn" onClick={onClose} aria-label="Закрыть">
          <X size={16} />
        </button>
      </div>

      <form className="acc-form" onSubmit={handleSubmit}>
        <div className="acc-field">
          <label htmlFor="reset-password">Новый пароль</label>
          <input
            id="reset-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            minLength={8}
            required
          />
          <span className="acc-hint">Минимум 8 символов</span>
        </div>

        <div className="acc-field">
          <label htmlFor="reset-confirm">Подтверждение пароля</label>
          <input
            id="reset-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
            required
          />
        </div>

        {error && <div className="acc-error" role="alert">{error}</div>}

        <button className="acc-btn-primary" type="submit" disabled={busy}>
          {busy ? <Loader2 size={14} className="spin" /> : <KeyRound size={14} />}
          {busy ? 'Сохранение…' : 'Сохранить пароль'}
        </button>

        {tokenInvalid && (
          <div className="acc-links">
            <button type="button" className="acc-link" onClick={() => onSwitchMode?.('forgot')}>
              Запросить сброс заново
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
